<template>
  <AkTooltipInformation
    v-if="shouldShowTooltip"
    :content="tooltipContent"
    :auto-hide="true"
    placement="bottom"
    trigger="hover"
    type="success"
    data-testid="credit-limit-pill-tooltip"
  >
    <a
      href="/v2/account/checkouts"
      class="credit-limit-pill"
      data-testid="credit-limit-pill"
      @click="trackPillClick"
    >
      <AkBadge
        :content="badgeConfig.content"
        :color="badgeConfig.color"
        :size="AkBadgeSize.Xs"
        :symbol="badgeConfig.symbol"
      />
    </a>
  </AkTooltipInformation>
  <a
    v-else
    href="/v2/account/checkouts"
    class="credit-limit-pill"
    data-testid="credit-limit-pill"
    @click="trackPillClick"
  >
    <AkBadge
      :content="badgeConfig.content"
      :color="badgeConfig.color"
      :size="AkBadgeSize.Xs"
      :symbol="badgeConfig.symbol"
    />
  </a>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { AkBadge, AkBadgeColor, AkBadgeSize, AkTooltipInformation } from '@ankorstore/design-system';
import { useCreditLimit } from '@modules/account/retailer-settings/composables/use-credit-limit';
import { useMediaQuery } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { UserClick } from '@ankorstore/events-tracking';
import Analytics from '@monolith/legacy/services/analytics';
import { useRegistrationNumberLabel } from '@modules/account/retailer-settings/utils/registration-number-label';
import { CreditLimitPillViewedEvent } from '@monolith/legacy/services/analytics/events/aria-payment.event';

enum PillState {
  Refused = 'refused',
  Frozen = 'frozen',
  Incomplete = 'incomplete',
  KycRequired = 'kyc_required',
  KycInReview = 'kyc_in_review',
  Active = 'active',
}

interface Props {
  // This component mounts twice at once (desktop #header + mobile #headerMobile, CSS-toggled by
  // the xl breakpoint, not v-if) — each parent passes whether ITS instance is the visible one so
  // impressions aren't double-counted. Defaults true for any other/standalone usage.
  isVisible?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isVisible: true,
});

const { t } = useI18n();
const store = useStore();
const countryCode = computed(() => store.getters.retailer.business.country.iso_code);
const registrationNumberLabel = useRegistrationNumberLabel(countryCode);
const {
  formattedAvailableAmount,
  formattedUnpaidAmount,
  creditLimitIncomplete,
  creditLimitFrozen,
  creditLimitRefused,
  creditLimitKycRequired,
  creditLimitKycInReview,
  isLegalInformationProtectedRefusal,
  hasMultipleProviders,
  activeProviderCount,
  isAriaPsp,
} = useCreditLimit();
const isCompact = useMediaQuery('(max-width: 1439px)');

const shouldShowTooltip = computed(
  () =>
    creditLimitRefused.value ||
    creditLimitFrozen.value ||
    creditLimitIncomplete.value ||
    hasMultipleProviders.value ||
    // KYC in-review has no tooltip yet (copy pending), so only the actionable required pill gets one.
    creditLimitKycRequired.value
);

const label = computed(() =>
  isCompact.value ? t('retailer.header.creditLimit.badge.mobile') : t('retailer.header.creditLimit.badge')
);

const tooltipContent = computed(() => {
  if (creditLimitKycRequired.value) {
    return t('retailer.header.creditLimit.badge.kyc.required.tooltip');
  }
  if (isLegalInformationProtectedRefusal.value) {
    return t('retailer.header.creditLimit.badge.refused.legalInformation.tooltip');
  }
  if (creditLimitRefused.value) {
    return t('retailer.header.creditLimit.badge.refused.tooltip', { registrationNumber: registrationNumberLabel.value });
  }
  if (creditLimitFrozen.value) {
    return formattedUnpaidAmount.value
      ? t('retailer.header.creditLimit.badge.frozen.tooltip.withAmount', { amount: formattedUnpaidAmount.value })
      : t('retailer.header.creditLimit.badge.frozen.tooltip');
  }
  if (creditLimitIncomplete.value) {
    const provider = isAriaPsp.value ? 'ARIA' : 'Ankorstore';
    return t('retailer.header.creditLimit.badge.tooltip', { provider });
  }
  if (hasMultipleProviders.value) {
    return t('retailer.header.creditLimit.badge.multiProvider.tooltip', { count: activeProviderCount.value });
  }
  return '';
});

const badgeConfig = computed(() => {
  if (creditLimitRefused.value) {
    return { content: t('retailer.header.creditLimit.badge.refused'), color: AkBadgeColor.RedFull, symbol: 'lock' };
  }
  // KYC pills reuse the settings badges — same label and visual across surfaces.
  if (creditLimitKycRequired.value) {
    return { content: t('retailer.settings.creditLimit.kyc.required.badge'), color: AkBadgeColor.RedFull, symbol: 'lock' };
  }
  if (creditLimitKycInReview.value) {
    return { content: t('retailer.settings.creditLimit.kyc.pending.badge'), color: AkBadgeColor.Cold, symbol: 'clock' };
  }

  const content = `${label.value} ${formattedAvailableAmount.value}`;

  if (creditLimitFrozen.value) {
    return { content, color: AkBadgeColor.GreyFull, symbol: 'exclamation-triangle' };
  }
  if (creditLimitIncomplete.value) {
    return { content, color: AkBadgeColor.Warning700, symbol: 'lock' };
  }
  return { content, color: AkBadgeColor.Blue, symbol: null };
});

const pillState = computed(() => {
  if (creditLimitRefused.value) {
    return PillState.Refused;
  }
  if (creditLimitFrozen.value) {
    return PillState.Frozen;
  }
  if (creditLimitIncomplete.value) {
    return PillState.Incomplete;
  }
  if (creditLimitKycRequired.value) {
    return PillState.KycRequired;
  }
  if (creditLimitKycInReview.value) {
    return PillState.KycInReview;
  }
  return PillState.Active;
});

const trackPillClick = () => {
  Analytics.track(UserClick({ component: 'credit_limit_pill', action: 'click', pill_state: pillState.value }));
};

// No dedup: impressions must recur per page nav for correct CTR-by-state math.
// Gated on isVisible so only the currently-visible desktop/mobile instance tracks (see Props).
watch(
  pillState,
  (state) => {
    if (props.isVisible) {
      Analytics.track(CreditLimitPillViewedEvent(state));
    }
  },
  { immediate: true }
);
</script>
